import Modal from '@/components/ui/Modal'
import MoodGauge from '@/features/market-rail/components/MoodGauge'
import { formatBsDate } from '@/lib/bsDate'
import type { CompanySentiment } from '@/schemas/company-profile'
import {
  SENTIMENT_BAND_WORDS,
  sentimentArt,
  sentimentBand,
} from '@/screens/stock/components/overview/sentimentBand'

interface CompanyMoodExplainerProps {
  symbol: string
  reading: CompanySentiment
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * "Fear and greed · NABIL" — what the Stock rail's reading opens (the mock's mood explainer, as
 * the market rail's opens `MoodExplainer`): the reading with Mitra in the band's pose, the
 * five-band track with its marker, the five words left to right, and the note that it is not a
 * signal. Only what mm-be serves: the score, its band and the day it was read. How the score is
 * worked out is mm-be's model, not described anywhere the app can quote, so it is not explained.
 */
export default function CompanyMoodExplainer({
  symbol,
  reading,
  open,
  onOpenChange,
}: CompanyMoodExplainerProps) {
  const band = sentimentBand(reading.label)
  const score = Math.round(reading.score)
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={`Fear and greed · ${symbol}`} size="560">
      <div className="flex flex-col gap-6 pt-5">
        <div className="flex items-center gap-4">
          <img
            src={sentimentArt(band)}
            alt=""
            className="h-22 w-18 shrink-0 object-contain"
            width={72}
            height={88}
          />
          <div className="flex flex-col gap-1">
            <p className="text-body-xs text-muted-foreground-subtle">
              {formatBsDate(reading.date)}
            </p>
            <p className="tnum text-h-2xl">
              {score}
              {band === null ? null : (
                <span className="text-h-section text-muted-foreground"> · {band.word}</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <MoodGauge
            score={reading.score}
            label={band?.word ?? 'no verdict'}
            subject={`${symbol} fear and greed`}
          />
          <div
            aria-hidden="true"
            className="flex justify-between text-body-xs text-muted-foreground-subtle"
          >
            {SENTIMENT_BAND_WORDS.map((word) => (
              <span key={word}>{word}</span>
            ))}
          </div>
        </div>

        <p className="text-body-m text-muted-foreground">
          A reading from 0 to 100 of the mood around {symbol}’s price: low is fearful, high is
          greedy.
        </p>

        <p className="text-body-xs text-muted-foreground-subtle">
          It reads the mood of the price. It is not a signal to buy or sell.
        </p>
      </div>
    </Modal>
  )
}
